import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { useAuth } from '../context/AuthContext';
import { createMembershipPayment, confirmMembershipPayment } from '../services/api';

// Add this to your api.js services
// export const createMembershipPayment = () => api.post('/payments/membership/create');
// export const confirmMembershipPayment = (data) => api.post('/payments/membership/confirm', data);

const stripePromise = loadStripe('pk_live_51TNHZ5HvGrrCwn9rLDoum3ZiM4Fz1VNrNHSP5ZzbIRh2wxc78BS7BKqcgnBAQXwoCnm2VhK6voQMKKAb9xoga0pD000N0U6R8I');

const CheckoutForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError('');
console.log('Payment started')
    try {
      // Create payment intent on backend
      const { data } = await createMembershipPayment();
       console.log('Payment intent created:', data); 
      const { clientSecret, paymentIntentId } = data;

      // Confirm payment with Stripe
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: `${user.first_name} ${user.last_name}`,
            email: user.email
          }
        }
      });

      if (result.error) {
        setError(result.error.message);
        return;
      }

      // Confirm with backend
      await confirmMembershipPayment({ paymentIntentId });
      setSuccess(true);
console.log('Stripe result:', result);
console.log('Payment intent status:', result.paymentIntent?.status);
      // Redirect to profile after 3 seconds
      setTimeout(() => navigate(`/profile/${user.id}`), 3000);

    } catch (err) {
      setError(err.response?.data?.error || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={styles.successCard}>
        <div style={styles.successIcon}>✓</div>
        <h2 style={styles.successTitle}>Welcome to Sytch!</h2>
        <p style={styles.successText}>
          Payment confirmed. Your background check has been initiated.
          You'll receive an email when it's complete — usually within 24-48 hours.
        </p>
        <p style={styles.redirectText}>Redirecting to your profile...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={styles.cardElementWrapper}>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#333',
                '::placeholder': { color: '#aab7c4' }
              }
            }
          }}
        />
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <button
        style={styles.payBtn}
        type="submit"
        disabled={!stripe || loading}
      >
        {loading ? 'Processing...' : 'Pay $19.99 — Become Verified'}
      </button>

      <p style={styles.secureNote}>
        🔒 Secured by Stripe. Your card details are never stored on our servers.
      </p>
    </form>
  );
};

const Membership = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) navigate('/login');
    if (user?.is_verified) navigate('/');
  }, [user, navigate]);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>Become a Verified Member</h2>
          <p style={styles.subtitle}>
            One-time fee of $19.99 activates your account permanently
          </p>
        </div>

        <div style={styles.benefits}>
          <div style={styles.benefit}>
            <span style={styles.benefitIcon}>✓</span>
            <span>Criminal background check included</span>
          </div>
          <div style={styles.benefit}>
            <span style={styles.benefitIcon}>✓</span>
            <span>Verified badge on your profile</span>
          </div>
          <div style={styles.benefit}>
            <span style={styles.benefitIcon}>✓</span>
            <span>Post and book trips on Sytch</span>
          </div>
          <div style={styles.benefit}>
            <span style={styles.benefitIcon}>✓</span>
            <span>One-time fee — never charged again</span>
          </div>
          <div style={styles.benefit}>
            <span style={styles.benefitIcon}>✗</span>
            <span style={styles.nonRefundable}>
              Non-refundable once background check is initiated
            </span>
          </div>
        </div>

        <div style={styles.paymentSection}>
          <h3 style={styles.paymentTitle}>Payment Details</h3>
          <Elements stripe={stripePromise}>
            <CheckoutForm />
          </Elements>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    padding: '40px 20px',
    fontFamily: 'Arial, sans-serif',
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '48px',
    width: '100%',
    maxWidth: '480px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1a1a2e',
    margin: '0 0 8px 0',
  },
  subtitle: {
    color: '#666',
    margin: '0',
  },
  benefits: {
    background: '#f8f9fa',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '32px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  benefit: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '15px',
    color: '#333',
  },
  benefitIcon: {
    color: '#4fc3f7',
    fontWeight: 'bold',
    fontSize: '16px',
    minWidth: '20px',
  },
  nonRefundable: {
    color: '#999',
    fontSize: '13px',
  },
  paymentSection: {
    borderTop: '1px solid #eee',
    paddingTop: '24px',
  },
  paymentTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#1a1a2e',
    margin: '0 0 20px 0',
  },
  cardElementWrapper: {
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '20px',
  },
  error: {
    background: '#ffebee',
    color: '#c62828',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
  },
  payBtn: {
    width: '100%',
    padding: '16px',
    background: '#4fc3f7',
    color: '#1a1a2e',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginBottom: '12px',
  },
  secureNote: {
    textAlign: 'center',
    color: '#999',
    fontSize: '13px',
    margin: '0',
  },
  successCard: {
    textAlign: 'center',
    padding: '20px',
  },
  successIcon: {
    width: '64px',
    height: '64px',
    background: '#e8f5e9',
    color: '#2e7d32',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    margin: '0 auto 20px auto',
  },
  successTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1a1a2e',
    margin: '0 0 12px 0',
  },
  successText: {
    color: '#555',
    lineHeight: '1.6',
    margin: '0 0 16px 0',
  },
  redirectText: {
    color: '#999',
    fontSize: '14px',
  },
};

export default Membership;
